import * as React from "react";

import {cn} from "@/lib/utils";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  containerClassName?: string;
  startContentClassName?: string;
  endContentClassName?: string;
  error?: string;
  showCharacterCount?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName = "",
      startContentClassName = "",
      endContentClassName = "",
      type,
      startContent,
      endContent,
      error,
      showCharacterCount,
      maxLength,
      onFocus,
      onBlur,
      value,
      ...props
    },
    ref,
  ) => {
    const [isInputFocused, setIsInputFocused] = React.useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsInputFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isInputFocused) {
        setIsInputFocused(false);
        onBlur?.(e);
      }
    };

    const shouldShowCharacterCount =
      showCharacterCount && isInputFocused && maxLength;
    const characterCount = maxLength
      ? maxLength - (String(value || "").length || 0)
      : 0;

    return (
      <div className={cn("relative", containerClassName)}>
        <div className="relative">
          {startContent && (
            <div
              className={cn(
                "absolute left-3 top-1/2 transform -translate-y-1/2",
                startContentClassName,
              )}
            >
              {startContent}
            </div>
          )}
          <input
            type={type}
            className={cn(
              "flex h-10 w-full rounded-md border border-status-invited-border bg-action-inactive-bg py-2 px-4 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-disabled focus-visible:outline-none  focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50",
              {"pl-8": startContent, "pr-8": endContent},
              error &&
                "placeholder:text-destructive border-destructive focus-visible:ring-0",
              className,
            )}
            ref={ref}
            maxLength={maxLength}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={value}
            {...props}
          />
          {endContent && (
            <div
              className={cn(
                "absolute right-3 top-1/2 transform -translate-y-1/2",
                endContentClassName,
              )}
            >
              {endContent}
            </div>
          )}
        </div>
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        {shouldShowCharacterCount && (
          <p className="text-xs text-secondary mt-1">
            {characterCount} characters left
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export {Input};
